import React, { Fragment } from 'react';
import { View, Image } from '@react-pdf/renderer';
import { mapValues, round, values } from 'lodash';
import { bilinearInterpolate, findAngle } from 'utilities/mathematic';
import devicePlots from 'globalConstants/devicePlots';
import { gradients } from 'utilities/color';
import { calculagteCeilingRelativePloptData, calculateWallRelativePlotData } from 'reduxModules/calculations/roomCalculations';

/**
 * Interface for area location
 */
interface AreaLocation {
  top: number;
  left: number;
  [key: string]: number;
}

/**
 * Interface for area dimensions
 */
interface AreaDimensions {
  length: number;
  width: number;
  [key: string]: number;
}

/**
 * Interface for subdivision (aisle)
 */
interface Subdivision {
  top: number;
  left: number;
  length: number;
  width: number;
  type?: string;
  [key: string]: any;
}

/**
 * Interface for plot data
 */
interface PlotData {
  id?: string;
  receiverSpacing?: number;
  receiverXOffset?: number;
  receiverYOffset?: number;
  mapArray?: any[];
  [key: string]: any;
}

/**
 * Interface for section (subdivision + data)
 */
interface Section {
  subdivision: Subdivision;
  data: PlotData;
}

/**
 * Interface for area
 */
interface Area {
  id: string;
  location: AreaLocation;
  dimensions: AreaDimensions;
  aislesArray?: Subdivision[];
  speakerLayout?: any[];
  [device: string]: any;
}

/**
 * Interface for plot properties
 */
interface PlotProps {
  source?: string;
  minLevel?: number;
  maxLevel?: number;
  accuracy?: number;
  pointToPixelValue?: (point: any, data: any) => number;
  [key: string]: any;
}

/**
 * Interface for style
 */
interface StyleProps {
  position: "absolute" | "relative" | undefined;
  top: number;
  left: number;
  width: number;
  height: number;
  [key: string]: any;
}

/**
 * Interface for plot
 */
interface Plot {
  style: StyleProps;
  image: string;
}

/**
 * Interface for processed area
 */
interface ProcessedArea {
  id: string;
  anchor: StyleProps;
  plots: Plot[];
}

/**
 * Interface for component props
 */
interface MuiCanvasProps {
  inContainer?: boolean;
  areaId?: string | null;
  content?: {
    areas?: Record<string, Area>;
    [key: string]: any;
  };
  device?: string;
  scale?: number;
  plotProps?: PlotProps;
  gradient?: Record<number, [number, number, number, number?]>;
  preGeneratedImages?: Record<string, GeneratedImage[]>;
  constants?: {
    equipment?: {
      cameras?: Record<string, any>;
      [key: string]: any;
    };
    [key: string]: any;
  };
}

/**
 * Interface for generated image data
 */
interface GeneratedImage {
  key: string;
  dataUrl: string;
  style: StyleProps;
}

/**
 * Generate plot data for devices that don't have pre-calculated data
 */
const generatePlotData = (
  device: string,
  deviceData: any,
  area: Area,
  plotProps: PlotProps,
  props?: any
): PlotData | null => {
  try {
    
    const { dimensions } = area;
    const roomLength = dimensions.length;
    const roomWidth = dimensions.width;

    // Create a reasonable grid size based on room dimensions
    const gridSpacing = 0.5; // 0.5 units between points
    const gridX = Math.ceil(roomLength / gridSpacing) + 1;
    const gridY = Math.ceil(roomWidth / gridSpacing) + 1;
    
    // For speakers, use the proper polar-pattern SPL calculation instead of
    // the naive distance-only formula, so the PDF heatmap matches the live app.
    if (device === 'speakers') {
      const layout = deviceData.layout || [];
      const constants = props?.constants;
      const model = deviceData.model;
      const speakerConfig = constants?.equipment?.speakers?.[model];

      if (layout.length > 0 && speakerConfig) {
        const GRID_SPACING = 0.5;
        const { top = 0, left = 0 } = area.location || {};
        const { length: areaLength, width: areaWidth } = area.dimensions;
        const gridXCount = Math.ceil((areaLength - left) / GRID_SPACING) + 1;
        const gridYCount = Math.ceil((areaWidth - top) / GRID_SPACING) + 1;
        const gridXOffset = round(((areaLength - left) - (gridXCount * GRID_SPACING)) / 2, 2);
        const gridYOffset = round(((areaWidth - top) - (gridYCount * GRID_SPACING)) / 2, 2);
        const grid = { gridSpacing: GRID_SPACING, gridXOffset, gridYOffset, gridX: gridXCount, gridY: gridYCount };

        if (speakerConfig.isCeilingSpkr || speakerConfig.isPendantSpkr) {
          return calculagteCeilingRelativePloptData(area, layout, grid, constants) || null;
        } else if (speakerConfig.isWallSpkr) {
          return calculateWallRelativePlotData(area, layout, grid, constants) || null;
        }
      }
    }

    // Generate mapArray based on device type
    const mapArray: number[][] = [];

    for (let y = 0; y < gridY; y++) {
      const row: number[] = [];
      for (let x = 0; x < gridX; x++) {
        const pointX = x * gridSpacing;
        const pointY = y * gridSpacing;
        let value = 0;
        
        switch (device) {
          case 'microphones':
            // Generate distance-based data for microphones
            if (deviceData.layout && deviceData.layout.length > 0) {
              // Calculate distance to nearest microphone
              let minDistance = Infinity;
              deviceData.layout.forEach((mic: any) => {
                const micX = mic.x || mic.location?.x || roomLength / 2;
                const micY = mic.y || mic.location?.y || roomWidth / 2;
                const distance = Math.sqrt(
                  Math.pow(pointX - micX, 2) + Math.pow(pointY - micY, 2)
                );
                minDistance = Math.min(minDistance, distance);
              });
              value = minDistance;
            } else {
              // Fallback: assume microphones in center of room
              const centerX = roomLength / 2;
              const centerY = roomWidth / 2;
              value = Math.sqrt(Math.pow(pointX - centerX, 2) + Math.pow(pointY - centerY, 2));
            }
            break;
            
          case 'speakers':
            // Generate SPL data for speakers
            if (deviceData.length > 0) {
              // Calculate combined SPL from all speakers
              let totalSPL = 0;
              deviceData.forEach((speaker: any) => {
                const speakerX = speaker.x || speaker.location?.x || roomLength / 2;
                const speakerY = speaker.y || speaker.location?.y || roomWidth / 2;
                const distance = Math.sqrt(
                  Math.pow(pointX - speakerX, 2) + Math.pow(pointY - speakerY, 2)
                );
                // SPL calculation: 85 dB at 1 unit, -6 dB per doubling of distance
                // Adjusted for better green gradient mapping
                const spl = Math.max(70, 85 - 6 * Math.log2(Math.max(0.5, distance)));
                totalSPL += Math.pow(10, spl / 10); // Add SPL logarithmically
              });
              value = 10 * Math.log10(totalSPL); // Convert back to dB
            } else {
              // Fallback: assume speakers positioned around the room perimeter
              const speakerPositions = [
                { x: roomLength * 0.15, y: roomWidth * 0.15 }, // Front left
                { x: roomLength * 0.85, y: roomWidth * 0.15 }, // Front right
                { x: roomLength * 0.15, y: roomWidth * 0.85 }, // Back left
                { x: roomLength * 0.85, y: roomWidth * 0.85 }  // Back right
              ];
              let totalSPL = 0;
              speakerPositions.forEach(speaker => {
                const distance = Math.sqrt(Math.pow(pointX - speaker.x, 2) + Math.pow(pointY - speaker.y, 2));
                const spl = Math.max(70, 85 - 6 * Math.log2(Math.max(0.5, distance)));
                totalSPL += Math.pow(10, spl / 10);
              });
              value = 10 * Math.log10(totalSPL);
            }
            break;
            
          case 'cameras':
            // Generate camera coverage data with smooth distance-based gradient within angular bounds
            if (deviceData.layout && deviceData.layout.length > 0) {
              const camera = deviceData.layout[0];
              const cameraX = camera.x || camera.location?.x || roomLength * 0.1;
              const cameraY = camera.y || camera.location?.y || roomWidth * 0.1;
              const aimX = camera.aimX || camera.aimPoint?.x || roomLength * 0.5;
              const aimY = camera.aimY || camera.aimPoint?.y || roomWidth * 0.5;

              // Get camera configuration from constants if available
              const cameraModel = deviceData.model || 'vidi250';
              const constants = props?.constants;
              const cameraConfig = constants?.equipment?.cameras?.[cameraModel];

              // For dual-imager cameras (like Vidi 280), use wideImager properties
              // For single-imager cameras, use direct properties
              const isDualImager = cameraConfig?.dualImager === true;
              const configAngle = isDualImager
                ? cameraConfig?.wideImager?.coverageAngle
                : cameraConfig?.coverageAngle;

              // Use coverageAngle from config, not from design (important for accuracy)
              const coverageAngle = configAngle || camera.coverageAngle || deviceData.coverageAngle;

              if (!coverageAngle) {
                // If no valid coverage angle, mark point as outside coverage
                value = Infinity;
              } else {

              // Calculate angle from camera to point
              const angle = findAngle(
                { x: aimX, y: aimY },
                { x: cameraX, y: cameraY },
                { x: pointX, y: pointY }
              );
              
              const halfCoverageAngle = coverageAngle / 2;
              
              if (angle >= halfCoverageAngle) {
                // Outside coverage angle - no coverage
                value = Infinity;
              } else {
                // Inside coverage angle - use distance from camera (like microphone coverage)
                // This creates the smooth distance-based gradient within the angular bounds
                value = Math.sqrt(Math.pow(pointX - cameraX, 2) + Math.pow(pointY - cameraY, 2));
              }
              }
            } else {
              // Fallback: camera in corner with standard coverage
              const cameraX = roomLength * 0.1;
              const cameraY = roomWidth * 0.1;
              const aimX = roomLength * 0.5;
              const aimY = roomWidth * 0.5;

              // Get camera configuration from constants if available
              const cameraModel = deviceData.model || 'vidi250';
              const constants = props?.constants;
              const cameraConfig = constants?.equipment?.cameras?.[cameraModel];

              // For dual-imager cameras (like Vidi 280), use wideImager properties
              // For single-imager cameras, use direct properties
              const isDualImager = cameraConfig?.dualImager === true;
              const configAngle = isDualImager
                ? cameraConfig?.wideImager?.coverageAngle
                : cameraConfig?.coverageAngle;

              const coverageAngle = configAngle || deviceData.coverageAngle;

              if (!coverageAngle) {
                value = Infinity;
              } else {
              
              const angle = findAngle(
                { x: aimX, y: aimY },
                { x: cameraX, y: cameraY },
                { x: pointX, y: pointY }
              );
              
              const halfCoverageAngle = coverageAngle / 2;
              
              if (angle >= halfCoverageAngle) {
                value = Infinity;
              } else {
                // Simple distance from camera within coverage angle
                value = Math.sqrt(Math.pow(pointX - cameraX, 2) + Math.pow(pointY - cameraY, 2));
              }
              }
            }
            break;
            
          default:
            value = 0;
        }
        
        row.push(value);
      }
      mapArray.push(row);
    }
    
    const plotData: PlotData = {
      id: `${area.id}-${device}-generated`,
      mapArray,
      receiverSpacing: gridSpacing,
      receiverXOffset: 0,
      receiverYOffset: 0
    };
    
    // Add camera-specific data
    if (device === 'cameras') {
      // Get camera configuration from constants if available
      const cameraModel = deviceData.model || 'vidi250';
      const constants = props?.constants;
      const cameraConfig = constants?.equipment?.cameras?.[cameraModel];

      // For dual-imager cameras (like Vidi 280), use wideImager properties
      // For single-imager cameras, use direct properties
      const isDualImager = cameraConfig?.dualImager === true;
      const configAngle = isDualImager
        ? cameraConfig?.wideImager?.coverageAngle
        : cameraConfig?.coverageAngle;

      if (deviceData.layout && deviceData.layout.length > 0) {
        const camera = deviceData.layout[0];
        plotData.camera = {
          location: {
            x: camera.x || camera.location?.x || roomLength * 0.1,
            y: camera.y || camera.location?.y || roomWidth * 0.1
          },
          aimPoint: {
            x: camera.aimX || camera.aimPoint?.x || roomLength * 0.5,
            y: camera.aimY || camera.aimPoint?.y || roomWidth * 0.5
          },
          // Use coverageAngle from config first (source of truth), not from design
          coverageAngle: configAngle || camera.coverageAngle || deviceData.coverageAngle
        };
      } else {
        // Fallback when no layout exists - use same defaults as coverage calculation
        plotData.camera = {
          location: {
            x: roomLength * 0.1,
            y: roomWidth * 0.1
          },
          aimPoint: {
            x: roomLength * 0.5,
            y: roomWidth * 0.5
          },
          // Use coverageAngle from config first (source of truth)
          coverageAngle: configAngle || deviceData.coverageAngle
        };
      }

      // Validate that we have a valid coverage angle
      if (!plotData.camera?.coverageAngle) {
        return null;
      }

      // Add camera configuration for dual-imager support
      if (cameraConfig) {
        plotData.cameraConfig = cameraConfig;
      }
    }
    
    return plotData;
  } catch (error) {
    console.error(`Error generating plot data for ${device}:`, error);
    return null;
  }
};

/**
 * Generate actual heatmap using the same logic as MuiHeatMap component
 */
const generateActualHeatmap = (
  width: number,
  height: number,
  scale: number,
  subdivision: Subdivision,
  data: PlotData,
  plotProps: PlotProps,
  gradient: Record<number, [number, number, number, number?]>
): string => {
  try {
    
    if (!data?.mapArray || !plotProps?.pointToPixelValue || !gradient) {
      console.warn('Missing required data for actual heatmap generation');
      return '';
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      console.error('Could not get canvas context');
      return '';
    }

    const { minLevel = 0, maxLevel = 100, accuracy = 100 } = plotProps;
    const heatmapimage = ctx.createImageData(width, height);
    const heatmapdata = heatmapimage.data;
    
    // Use the exact same algorithm as MuiHeatMap
    for (let i = 0; i < width * height; i++) {
      const y = Math.floor(i / width);
      const x = i - y * width;
      const dx = x / scale + subdivision.left;
      const dy = y / scale + subdivision.top;
      
      try {
        let pixelLevel = plotProps.pointToPixelValue({ x: dx, y: dy, px: x, py: y }, data);
        
        // Handle special cases like Infinity (for camera coverage outside angle)
        if (!isFinite(pixelLevel)) {
          continue; // Skip pixels that are outside coverage area
        }
        
        pixelLevel = Math.max(minLevel, Math.min(maxLevel, pixelLevel));
        
        const index = round(pixelLevel * accuracy);
        const rgb = gradient[index];
        const baseindex = 4 * i;

        if (!rgb || rgb.length < 3) continue;

        // Apply custom opacity if provided in data (for dual-imager cameras)
        const baseOpacity = rgb[3] ?? 1;
        const finalOpacity = data.opacity !== undefined ? data.opacity * baseOpacity : baseOpacity;

        heatmapdata[baseindex] = rgb[0]; // red
        heatmapdata[baseindex + 1] = rgb[1]; // green
        heatmapdata[baseindex + 2] = rgb[2]; // blue
        heatmapdata[baseindex + 3] = finalOpacity * 255; // alpha with custom opacity
      } catch (pixelError) {
        console.error(`Error processing pixel ${i}:`, pixelError);
      }
    }

    ctx.putImageData(heatmapimage, 0, 0);
    const dataUrl = canvas.toDataURL('image/png');
    
    return dataUrl;
  } catch (error) {
    console.error('Error generating actual heatmap image:', error);
    return '';
  }
};

/**
 * MUI Canvas section component that replaces the styled-components Canvas section
 * 
 * Renders heatmap visualizations in PDF using the actual plot data system from the live app.
 * 
 * @param {MuiCanvasProps} props - Component props
 * @returns {JSX.Element} MUI Canvas section component
 */
const MuiCanvas: React.FC<MuiCanvasProps> = (props) => {
  const { 
    inContainer = false, 
    areaId = null, 
    content = {}, 
    device, 
    scale = 1, 
    plotProps,
    gradient
  } = props;
  
  const areas = content.areas || {};
  const areaArray = inContainer ? values(areas) : (areaId && areas[areaId] ? [areas[areaId]] : []);
  
  // Process areas using the actual plot data system
  const processedAreas: ProcessedArea[] = areaArray.map(area => {
    const { id, location, dimensions } = area;
    const { top, left } = mapValues(location, (v) => round(v * scale));
    const { length, width } = mapValues(dimensions, (v) => round(v * scale));

    const anchor: StyleProps = {
      position: 'absolute',
      top: inContainer ? top : 0,
      left: inContainer ? left : 0,
      width: length,
      height: width,
    };

    const plots: Plot[] = [];
    
    if (device && plotProps && gradient && area[device]) {
      // Skip camera heatmap if camera is 'none' or not enabled
      if (device === 'cameras') {
        const cameras = area[device];
        if (!cameras.hasCamera || cameras.model === 'none') {
          // Don't render camera heatmap for 'none' cameras
          return { id, location, anchor, plots: [] };
        }
      }
      
      // Check if we have actual plot data (like the live app uses)
      const deviceData = area[device];
      let plotData = deviceData?.plotData;
      
      // Handle different device data structures
      if (!plotData) {
        // For speakers, check if we have speakerLayout with plot data
        if (device === 'speakers' && area.speakerLayout) {
          // Use the first layout item's plot data
          const layoutItem = area.speakerLayout[0];
          if (layoutItem && layoutItem[plotProps.source || 'directSPL']) {
            plotData = {
              id: layoutItem.id,
              receiverSpacing: layoutItem.receiverSpacing,
              receiverXOffset: layoutItem.receiverXOffset,
              receiverYOffset: layoutItem.receiverYOffset,
              mapArray: layoutItem[plotProps.source || 'directSPL']
            };
          }
        }
        
        // For microphones, check the microphones object
        if (device === 'microphones' && deviceData?.plotData) {
          plotData = deviceData.plotData;
        }
        
        // For cameras, check cameras object and handle dual-imager cameras
        if (device === 'cameras' && deviceData?.plotData) {
          plotData = { ...deviceData.plotData };
        }
      }

      // For ceiling/pendant/wall speakers, always recompute locally so the
      // relativeAverage gradient is based on non-linearly-scaled values.
      // HearSpace stores raw absolute SPL in mapArray (no scaling), which produces
      // red in the heatmap when used directly. The live app also ignores HearSpace
      // mapArray for these types and always calls calculagteCeilingRelativePloptData
      // / calculateWallRelativePlotData (see getRelativeAverageData in container.ts).
      if (device === 'speakers' && plotData && props?.constants) {
        const speakerConfig = props.constants?.equipment?.speakers?.[deviceData?.model];
        if (speakerConfig?.isCeilingSpkr || speakerConfig?.isPendantSpkr || speakerConfig?.isWallSpkr) {
          plotData = null;
        }
      }

      // Special handling for dual-imager cameras (like Vidi 280)
      if (device === 'cameras' && plotData) {
        const cameraConfig = plotData.cameraConfig || deviceData?.cameraConfig;
        const isDualImager = cameraConfig?.dualImager === true;

        if (isDualImager) {

          // Generate TWO separate plots (wide and zoom) for dual-imager cameras
          const subdivision: Subdivision = {
            top: 0,
            left: 0,
            length: 0,
            width: 0
          };

          // Calculate room size for opacity adjustment
          const roomDiagonal = Math.sqrt(dimensions.length * dimensions.length + dimensions.width * dimensions.width);
          const isSmallRoom = roomDiagonal < 8; // Less than ~8 meters diagonal

          // Adjust opacity based on room size for better visibility
          // In small rooms, make wide more transparent to emphasize zoom
          const wideOpacity = isSmallRoom ? 0.3 : 0.4;
          const zoomOpacity = isSmallRoom ? 0.7 : 0.6;

          // Wide-angle imager plot
          const widePlotData = {
            ...plotData,
            id: `${plotData.id}-wide`,
            camera: {
              ...plotData.camera,
              coverageAngle: cameraConfig.wideImager.coverageAngle,
              coverageRadius: cameraConfig.wideImager.coverageRadius,
              limitRadius: cameraConfig.wideImager.limitRadius
            },
            cameraConfig: cameraConfig.wideImager,
            opacity: wideOpacity
          };

          // Zoom imager plot
          const zoomPlotData = {
            ...plotData,
            id: `${plotData.id}-zoom`,
            camera: {
              ...plotData.camera,
              coverageAngle: cameraConfig.zoomImager.coverageAngle,
              coverageRadius: cameraConfig.zoomImager.coverageRadius,
              limitRadius: cameraConfig.zoomImager.limitRadius
            },
            cameraConfig: cameraConfig.zoomImager,
            opacity: zoomOpacity
          };

          // Generate wide plot gradient with its specific properties
          const wideGradient = (gradients as any)['cameraCoverage']({
            ...plotProps,
            coverageRadius: cameraConfig.wideImager.coverageRadius,
            limitRadius: cameraConfig.wideImager.limitRadius,
            opacity: widePlotData.opacity
          } as any);

          // Generate zoom plot gradient with its specific properties
          const zoomGradient = (gradients as any)['cameraCoverage']({
            ...plotProps,
            coverageRadius: cameraConfig.zoomImager.coverageRadius,
            limitRadius: cameraConfig.zoomImager.limitRadius,
            opacity: zoomPlotData.opacity
          } as any);

          // Render wide plot
          const wideImageUrl = generateActualHeatmap(
            length,
            width,
            scale,
            subdivision,
            widePlotData,
            plotProps,
            wideGradient
          );

          if (wideImageUrl) {
            console.log('Canvas: Wide plot generated successfully');
            plots.push({
              style: {
                position: 'absolute',
                top: 0,
                left: 0,
                width: length,
                height: width
              },
              image: wideImageUrl
            });
          } else {
            console.warn('Canvas: Wide plot generation failed');
          }

          // Render zoom plot (on top)
          const zoomImageUrl = generateActualHeatmap(
            length,
            width,
            scale,
            subdivision,
            zoomPlotData,
            plotProps,
            zoomGradient
          );

          if (zoomImageUrl) {
            console.log('Canvas: Zoom plot generated successfully');
            plots.push({
              style: {
                position: 'absolute',
                top: 0,
                left: 0,
                width: length,
                height: width
              },
              image: zoomImageUrl
            });
          } else {
            console.warn('Canvas: Zoom plot generation failed');
          }

          // Skip the standard single-plot rendering below
          return {
            id: `${id}-heatmap`,
            anchor,
            plots,
          };
        }
      }

      if (plotData?.mapArray && plotProps.pointToPixelValue) {

        // For single-imager cameras, override camera properties with config values
        if (device === 'cameras') {
          // IMPORTANT: Get camera config from constants (source of truth)
          // Don't rely on plotData.cameraConfig which might not exist in old saved designs
          const cameraModel = deviceData.model || 'vidi250';
          const constants = props?.constants;
          const cameraConfig = constants?.equipment?.cameras?.[cameraModel];

          // Override with config values (source of truth) instead of using design values
          if (cameraConfig && !cameraConfig.dualImager) {
            plotData = {
              ...plotData,
              camera: {
                ...plotData.camera,
                coverageAngle: cameraConfig.coverageAngle,
                coverageRadius: cameraConfig.coverageRadius,
                limitRadius: cameraConfig.limitRadius
              },
              cameraConfig: cameraConfig
            };
          }
        }

        // Create subdivision (same as MuiHeatMap)
        const subdivision: Subdivision = {
          top: 0,
          left: 0,
          length: 0,
          width: 0
        };

        // Generate the actual heatmap using real plot data
        const imageDataUrl = generateActualHeatmap(
          length,
          width,
          scale,
          subdivision,
          plotData,
          plotProps,
          gradient
        );
        
        if (imageDataUrl) {
          plots.push({
            style: {
              position: 'absolute',
              top: 0,
              left: 0,
              width: length,
              height: width
            },
            image: imageDataUrl
          });
        } else {
          console.warn(`Canvas: Failed to generate heatmap image for ${device} in area ${id}`);
        }
      } else {
        // Skip plot generation for cameras when model is 'none' or not set
        if (device === 'cameras' && (!deviceData.hasCamera || deviceData.model === 'none')) {
          return { id, location, anchor, plots: [] };
        }
        
        console.warn(`Canvas: Missing plot data for ${device} in area ${id}, generating plot data...`);

        // Generate plot data on-demand
        const generatedPlotData = generatePlotData(device, deviceData, area, plotProps, props);
        
        if (generatedPlotData && plotProps.pointToPixelValue) {
          
          // Create subdivision (same as MuiHeatMap)
          const subdivision: Subdivision = { 
            top: 0, 
            left: 0, 
            length: 0, 
            width: 0 
          };
          
          // Generate the actual heatmap using generated plot data
          const imageDataUrl = generateActualHeatmap(
            length,
            width,
            scale,
            subdivision,
            generatedPlotData,
            plotProps,
            gradient
          );
          
          if (imageDataUrl) {
            plots.push({
              style: {
                position: 'absolute',
                top: 0,
                left: 0,
                width: length,
                height: width
              },
              image: imageDataUrl
            });
          } else {
            console.warn(`Canvas: Failed to generate heatmap from generated data for ${device} in area ${id}`);
          }
        } else {
          console.error(`Canvas: Failed to generate plot data for ${device} in area ${id}`);
          
          // Create a diagnostic placeholder to show what data is missing
          const canvas = document.createElement('canvas');
          canvas.width = length;
          canvas.height = width;
          const ctx = canvas.getContext('2d');
          
          if (ctx) {
            ctx.fillStyle = 'rgba(255, 0, 0, 0.3)'; // Red to indicate missing data
            ctx.fillRect(0, 0, length, width);
            
            ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
            ctx.font = '10px Arial';
            ctx.textAlign = 'center';
            ctx.fillText(`${device.toUpperCase()}`, length / 2, width / 2 - 15);
            ctx.fillText('FAILED TO GENERATE', length / 2, width / 2);
            ctx.fillText(`Area: ${id}`, length / 2, width / 2 + 15);
            
            const dataUrl = canvas.toDataURL('image/png');
            plots.push({
              style: {
                position: 'absolute',
                top: 0,
                left: 0,
                width: length,
                height: width
              },
              image: dataUrl
            });
          }
        }
      }
    } else {
      console.warn(`Canvas: Missing required props or device data:`, {
        hasDevice: !!device,
        hasPlotProps: !!plotProps,
        hasGradient: !!gradient,
        hasDeviceData: !!(area[device || ''])
      });
    }

    return {
      id: `${id}-heatmap`,
      anchor,
      plots,
    };
  });

  
  return (
    <Fragment>
      {processedAreas.map((area, index) => (
        <View key={index} style={area.anchor}>
          {area.plots.map((sub, i) => (
            sub.image ? (
              <Image key={`subplot-${i}`} src={sub.image} style={sub.style} />
            ) : null
          ))}
        </View>
      ))}
    </Fragment>
  );
};

// Note: The getImage function has been moved to canvasImageService.ts
// for pre-generation of images before PDF rendering. This avoids
// canvas operation issues during PDF generation.

export default MuiCanvas;
